/**
 * 上传队列的数据结构。
 *
 * 队列是整个工人端的命脉：工地经常没信号，拍完照、打完卡不能指望当场传上去。
 * 所有写操作都先落到本地队列，UI 立刻给出成功反馈，后台 Worker 慢慢传。
 *
 * 两条硬约束：
 * 1. 每个条目带 clientId（服务端幂等键），重传不会产生重复记录
 * 2. 每个条目记 userId（CR-003）—— 手机多人共用，A 的待传数据
 *    绝不能在 B 登录后被当成 B 的数据传上去
 */

export type QueueKind = 'PUNCH' | 'HIDDEN_WORK' | 'DAILY_LOG' | 'PURCHASE';

export type QueueStatus =
  /** 等待上传 */
  | 'PENDING'
  /** 正在传，进程被杀后会在启动时复位成 PENDING */
  | 'UPLOADING'
  /** 传完了，很快会被清理 */
  | 'DONE'
  /** 重试到上限仍失败，需要人介入 */
  | 'FAILED';

/** 附件种类，和服务端 `AttachKind` 对齐。presign 要按它判大小和类型白名单。 */
export type QueuedFileKind = 'PHOTO' | 'VIDEO' | 'RECEIPT';

/** 队列里待传的一个文件。blob 直接存 IndexedDB，原生和浏览器同一套实现。 */
export interface QueuedFile {
  id: string;
  /** 所属队列条目 */
  itemId: string;
  /** 服务端幂等键，登记附件时用 */
  clientId: string;
  blob: Blob;
  mimeType: string;
  /**
   * 入队时就定好，不在上传时猜（CR-023）。
   * 改之前是 `item.kind === 'PURCHASE' ? 'RECEIPT' : 'PHOTO'` ——
   * 视频会被当成照片去 presign，撞上 PHOTO 的 20MB 上限和图片白名单，
   * 报出来的却是「不支持的文件类型」。
   */
  kind?: QueuedFileKind;
  sizeBytes: number;
  /** 原图校验和，证明未被篡改 */
  sha256: string;
  /** 直传完成后填上，业务提交时带给服务端 */
  fileKey?: string;
}

export interface QueueItem {
  /** 同时是服务端的幂等键 */
  id: string;
  userId: number;
  siteId: number;
  kind: QueueKind;
  /** 业务数据，不含文件 */
  payload: Record<string, unknown>;
  status: QueueStatus;
  attempts: number;
  /** 下次可以尝试的时间（epoch ms），退避用 */
  nextAttemptAt: number;
  /** 最后一次失败的错误码，给 UI 翻译用 */
  lastError?: string;
  createdAt: number;
  updatedAt: number;
}

/** 给 UI 看的汇总 */
export interface QueueSnapshot {
  pending: number;
  uploading: number;
  failed: number;
  /** 待传的文件总数，UI 上比条目数更有体感 */
  pendingFiles: number;
  items: QueueItem[];
}

export const EMPTY_SNAPSHOT: QueueSnapshot = {
  pending: 0,
  uploading: 0,
  failed: 0,
  pendingFiles: 0,
  items: [],
};
